<template>
  <section class="po-picker" aria-label="Schedule capacity">
    <label for="po-search">Purchase Order (optional)</label>
    <v-autocomplete
      id="po-search"
      :model-value="selectedPoNumber"
      :items="schedulableItems"
      :item-props="itemProps"
      item-title="poNumber"
      item-value="poNumber"
      :loading="poStore.loading"
      placeholder="Type to search PO numbers"
      persistent-placeholder
      no-filter
      clearable
      hide-details
      variant="outlined"
      density="compact"
      prepend-inner-icon="mdi-magnify"
      :no-data-text="noDataText"
      @update:model-value="onPoChange"
      @update:search="onSearch"
    />
    <p v-if="poStore.error" class="po-picker__error" role="alert">{{ poStore.error }}</p>

    <section v-if="selectedPo" class="po-picker__detail" aria-label="Selected purchase order">
      <div class="po-picker__detail-heading">
        <strong>
          {{ selectedPo.poNumber }}
          <span
            v-if="selectedPo.productCategory"
            class="po-picker__category"
            :style="{ background: categoryTagFor(selectedPo.productCategory).background, color: categoryTagFor(selectedPo.productCategory).color }"
          >
            {{ categoryTagFor(selectedPo.productCategory).label }}
          </span>
        </strong>
      </div>
      <div class="po-picker__totals">
        <span v-for="(total, code) in selectedPo.terminationTotals" :key="code" class="po-picker__total" :class="{ 'po-picker__total--match': code === typeCode }">
          {{ totalLabelFor(selectedPo, code, total) }}
        </span>
        <span v-if="selectedPo.estimatedTotal > 0" class="po-picker__total po-picker__total--estimate" title="Estimated from Odoo fiber count">
          ~{{ selectedPo.estimatedTotal.toLocaleString() }} est.
        </span>
      </div>
      <p v-for="warning in selectedPo.warnings" :key="warning" class="po-picker__hint po-picker__hint--warning">{{ warning }}</p>

      <!-- A non-manufacturing PO gets no quantity field at all: it is planned into the
           week for visibility and can never draw down capacity. -->
      <p v-if="isNonCapacityPo(selectedPo.poNumber)" class="po-picker__hint">
        {{ selectedPo.productCategory ?? 'Non-manufacturing' }} PO — planned into this week without consuming {{ typeCode }} capacity.
      </p>
      <template v-else-if="selectedPo.poNumber in selected">
        <div v-for="(quantity, typeId) in selected[selectedPo.poNumber]" :key="typeId" class="po-picker__qty-row">
          <label :for="`po-qty-${selectedPo.poNumber}-${typeId}`">Quantity ({{ typeCodeForId(typeId) }})</label>
          <input
            :id="`po-qty-${selectedPo.poNumber}-${typeId}`"
            v-model.number="selected[selectedPo.poNumber][typeId]"
            type="number"
            :min="0"
            class="po-picker__qty-input"
          />
        </div>
        <p v-if="isResize(selectedPo.poNumber)" class="po-picker__hint">
          Replaces the {{ assignedQuantityFor(selectedPo.poNumber).toLocaleString() }} already assigned here — other weeks and capacity types keep theirs.
        </p>
        <p v-else-if="allowsZeroQuantity(selectedPo.poNumber) && (selected[selectedPo.poNumber][capacityTypeId] ?? 0) === 0" class="po-picker__hint">
          Not counted toward capacity — enter a quantity to count it.
        </p>
      </template>
    </section>

    <template v-else>
      <label for="schedule-quantity">Quantity ({{ typeCode }})</label>
      <input id="schedule-quantity" v-model.number="manualQuantity" type="number" min="1" class="po-picker__qty-input" />
    </template>

    <label for="schedule-notes">{{ notesLabel }}</label>
    <textarea id="schedule-notes" v-model="manualNotes" rows="2" placeholder="e.g. planned maintenance" />

    <footer class="po-picker__footer">
      <p v-if="selectedPo" class="po-picker__summary" :class="{ 'po-picker__summary--over': overCapacity }">
        <template v-if="selectedTotal < 0">Returns {{ Math.abs(selectedTotal).toLocaleString() }} to this week</template>
        <template v-else>Scheduling {{ selectedTotal.toLocaleString() }} of {{ remainingCapacity.toLocaleString() }} remaining</template>
        <template v-if="overCapacity">— over capacity (allowed, the week will show as overbooked)</template>
      </p>
      <div class="po-picker__actions">
        <button type="button" class="po-picker__btn-secondary" @click="emit('close')">Cancel</button>
        <button type="button" class="po-picker__btn-primary" :disabled="!canSchedule || submitting" @click="schedule">
          {{ submitting ? 'Scheduling…' : 'Schedule' }}
        </button>
      </div>
    </footer>

    <ConfirmDialog
      :open="pendingOverbook !== null"
      title="Overbook this week?"
      :message="overbookMessage"
      confirm-label="Assign anyway"
      @confirm="submitConfirmed"
      @cancel="pendingOverbook = null"
    />
  </section>
</template>

<script setup lang="ts">
  import { computed } from 'vue';

  import ConfirmDialog from '@/components/common/ConfirmDialog.vue';
  import { usePoAssignment } from '@/composables/usePoAssignment';
  import { usePurchaseOrderStore } from '@/store/purchaseOrderStore';
  import type { CapacityCategory } from '@/types/capacityType';
  import type { PurchaseOrder } from '@/types/purchaseOrders';
  import { categoryTagFor } from '@/utils/categoryTags';

  const props = defineProps<{
    vendorId: string;
    capacityTypeId: string;
    typeCode: string;
    rowId: string;
    weekKey: string;
    remainingCapacity: number;
    assignedPoNumbers: string[];
    // The target allocation's category — undefined defaults to termination behavior.
    // A molding target pre-fills 0 and shows the "not counted" hint at 0 regardless
    // of the PO's isCapacityRelevant flag (see usePoAssignment's allowsZeroQuantity).
    category?: CapacityCategory;
  }>();

  const emit = defineEmits<{ close: [] }>();

  // The sidebar offers confirmed/active POs only — RFQs cannot be scheduled.
  // Set before usePoAssignment so its initial load already uses these states.
  const purchaseOrderStore = usePurchaseOrderStore();
  purchaseOrderStore.states = ['purchase', 'done'];

  const {
    poStore,
    schedulableItems,
    selected,
    selectExclusive,
    submitting,
    selectedTotal,
    overCapacity,
    canSubmit,
    pendingOverbook,
    missingTypeCodesFor,
    isSelectable,
    isResize,
    assignedQuantityFor,
    allowsZeroQuantity,
    isNonCapacityPo,
    typeCodeForId,
    defaultQuantity,
    requestSubmit,
    submitConfirmed,
    manualQuantity,
    manualNotes,
    canSubmitManual,
    submitManualBlock,
  } = usePoAssignment(() => props);

  // The `selected` record stays the single source of truth: the composable's
  // prune-on-filter-change watches keep working, and the dropdown just mirrors it.
  const selectedPoNumber = computed(() => Object.keys(selected.value)[0] ?? null);
  const selectedPo = computed(() => poStore.items.find((po) => po.poNumber === selectedPoNumber.value) ?? null);

  const notesLabel = computed(() => (selectedPoNumber.value ? 'Comment (optional)' : 'Comment (required)'));
  const canSchedule = computed(() => (selectedPoNumber.value ? canSubmit.value : canSubmitManual.value));
  const noDataText = computed(() => (poStore.loading ? 'Loading purchase orders…' : 'No schedulable POs — schedule without a PO by adding a comment below.'));

  function onPoChange(poNumber: string | null): void {
    selectExclusive(poNumber ? (poStore.items.find((po) => po.poNumber === poNumber) ?? null) : null);
  }

  // poStore.search drives the debounced server-side reload in usePoAssignment.
  function onSearch(value: string): void {
    poStore.search = value;
  }

  function itemProps(po: PurchaseOrder) {
    return { subtitle: subtitleFor(po), disabled: !isSelectable(po) };
  }

  // Remaining availability, not the full order — a partially scheduled PO reads
  // "LC 3,000 of 10,000"; an untouched one keeps the plain total.
  function totalLabelFor(po: PurchaseOrder, code: string, total: number): string {
    const remaining = po.remainingTotals?.[code];
    if (remaining === undefined || remaining === total) return `${code} ${total.toLocaleString()}`;
    return `${code} ${Math.max(remaining, 0).toLocaleString()} of ${total.toLocaleString()}`;
  }

  function subtitleFor(po: PurchaseOrder): string {
    const parts: string[] = [];
    const missing = missingTypeCodesFor(po);
    if (missing.length > 0) {
      parts.push(`no ${missing.join(', ')} allocation${missing.length > 1 ? 's' : ''} this week`);
    } else if (isResize(po.poNumber)) {
      parts.push(`already in this week · ${assignedQuantityFor(po.poNumber).toLocaleString()} ${props.typeCode}`);
    } else if (!po.isCapacityRelevant) {
      parts.push(`${po.productCategory ?? 'non-manufacturing'} — does not consume capacity`);
    } else if (props.category !== 'molding') {
      const remaining = defaultQuantity(po);
      if (remaining > 0) parts.push(`${remaining.toLocaleString()} ${props.typeCode} left to schedule`);
      else if (po.remainingTotals === null) parts.push('termination count unknown');
    }
    return parts.join(' · ');
  }

  function schedule(): void {
    if (selectedPoNumber.value) {
      void requestSubmit();
      return;
    }
    void submitManualBlock();
  }

  function formatWeek(weekKey: string): string {
    const [year, month, day] = weekKey.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  }

  const overbookMessage = computed(() => {
    const overbooked = pendingOverbook.value;
    if (!overbooked) return '';
    const list = overbooked.map((entry) => `${entry.typeCode} by ${entry.by.toLocaleString()}`).join(', ');
    return `This will overbook ${list} for the week of ${formatWeek(props.weekKey)} — this may cause problems. Assign anyway?`;
  });
</script>

<style scoped>
  .po-picker {
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
    padding-top: 1rem;
  }

  .po-picker > label {
    color: rgb(100 116 139);
    font-size: 0.75rem;
  }

  /* Tailwind's preflight zeroes border-width globally, which flattens
     Vuetify's notched outline (it never redraws visibly even after
     restoring --v-field-border-width). Draw a plain border on the field
     wrapper instead, matching the Comment textarea below it, and hide
     Vuetify's own outline parts so they don't double-draw. */
  .po-picker :deep(.v-field) {
    border: 1px solid rgb(203 213 225);
    border-radius: 0.375rem;
    background: white;
  }

  .po-picker :deep(.v-field--focused) {
    border-color: rgb(37 99 235);
  }

  .po-picker :deep(.v-field__outline) {
    display: none;
  }

  .po-picker textarea {
    padding: 0.375rem 0.5rem;
    border: 1px solid rgb(203 213 225);
    border-radius: 0.375rem;
    font: inherit;
    resize: vertical;
  }

  .po-picker__error {
    margin: 0;
    color: rgb(185 28 28);
    font-size: 0.8125rem;
  }

  .po-picker__hint {
    margin: 0;
    color: rgb(100 116 139);
    font-size: 0.8125rem;
  }

  .po-picker__hint--warning {
    color: rgb(180 83 9);
  }

  .po-picker__detail {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.75rem;
    border: 1px solid rgb(226 232 240);
    border-radius: 0.5rem;
    background: rgb(248 250 252);
  }

  .po-picker__detail-heading {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.75rem;
  }

  .po-picker__detail-heading strong {
    font-size: 0.875rem;
  }

  .po-picker__category {
    padding: 0 0.375rem;
    border-radius: 9999px;
    font-size: 0.625rem;
    font-weight: 600;
    line-height: 1.5;
  }

  .po-picker__totals {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.375rem;
  }

  .po-picker__total {
    color: rgb(148 163 184);
    font-size: 0.75rem;
    white-space: nowrap;
  }

  .po-picker__total--match {
    color: rgb(15 23 42);
    font-weight: 700;
  }

  .po-picker__total--estimate {
    color: rgb(180 83 9);
    font-style: italic;
  }

  .po-picker__qty-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .po-picker__qty-row label {
    color: rgb(100 116 139);
    font-size: 0.75rem;
  }

  .po-picker__qty-input {
    width: 7rem;
    padding: 0.25rem 0.5rem;
    border: 1px solid rgb(203 213 225);
    border-radius: 0.375rem;
    font-size: 0.8125rem;
    text-align: right;
  }

  .po-picker__footer {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .po-picker__summary {
    margin: 0;
    color: rgb(100 116 139);
    font-size: 0.75rem;
  }

  .po-picker__summary--over {
    color: rgb(180 83 9);
    font-weight: 600;
  }

  .po-picker__actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
  }

  .po-picker__btn-secondary {
    min-height: 2.5rem;
    padding: 0.5rem 0.875rem;
    border-radius: 0.375rem;
    color: rgb(71 85 105);
    font-size: 0.875rem;
    font-weight: 500;
  }

  .po-picker__btn-secondary:hover {
    background: rgb(241 245 249);
  }

  .po-picker__btn-primary {
    min-height: 2.5rem;
    padding: 0.5rem 0.875rem;
    border-radius: 0.375rem;
    background: rgb(37 99 235);
    color: white;
    font-size: 0.875rem;
    font-weight: 600;
  }

  .po-picker__btn-primary:hover {
    background: rgb(29 78 216);
  }

  .po-picker__btn-primary:disabled {
    background: rgb(226 232 240);
    color: rgb(148 163 184);
    cursor: not-allowed;
  }
</style>
